// TabErrorBoundary — wraps a single tab's content on Deal Detail so that a render
// crash in one tab (e.g. the Phase-33 `fmt is not defined` regression in
// NegotiationTab) cannot take down the rest of the page. The boundary keeps the
// other tabs interactive, surfaces a friendly recovery card, lets the user
// re-mount just this tab via a "Reload tab" button (state-keyed remount, no
// page reload), and offers a one-click "Report issue" mailto with the error
// message + URL pre-populated. Console logs include tabName + componentStack so
// the next regression is diagnosable.

import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Mail } from "lucide-react";

class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const tabName = this.props.tabName || "unknown";
    // eslint-disable-next-line no-console
    console.error(
      `[TabErrorBoundary] Tab "${tabName}" crashed:`,
      error,
      info && info.componentStack ? info.componentStack : ""
    );
  }

  handleReload = () => {
    this.setState((s) => ({
      hasError: false,
      error: null,
      resetKey: s.resetKey + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      const tabName = this.props.tabName || "this";
      const errMsg =
        (this.state.error && (this.state.error.message || String(this.state.error))) ||
        "Unknown error";
      const subject = encodeURIComponent(
        `[RunIdea.ai] Crash report — ${tabName} tab`
      );
      const body = encodeURIComponent(
        `Hi RunIdea team,\n\nThe "${tabName}" tab crashed with:\n\n${errMsg}\n\nURL: ${
          typeof window !== "undefined" ? window.location.href : ""
        }\n\n— sent from the in-app crash card`
      );
      return (
        <div
          data-testid={`tab-error-${tabName}`}
          className="rounded-2xl border border-amber-200 bg-amber-50/40 p-8 text-center shadow-sm"
        >
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">
            Something went wrong loading the {tabName} tab
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            Other tabs on this page should still work. You can try reloading
            just this tab — the rest of the deal stays open.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={this.handleReload}
              data-testid={`tab-error-${tabName}-reload`}
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Reload tab
            </Button>
            <a
              href={`mailto:admin@runidea.ai?subject=${subject}&body=${body}`}
              data-testid={`tab-error-${tabName}-report`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 underline-offset-4 hover:text-indigo-600 hover:underline"
            >
              <Mail className="h-3.5 w-3.5" /> Report issue
            </a>
          </div>
        </div>
      );
    }

    // Re-key the subtree on reload so the children fully unmount + remount.
    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}

export default TabErrorBoundary;
